import React, { useState, useEffect, useRef } from "react";
import { Box, Tabs, Tab, IconButton, Tooltip } from "@mui/material";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import ReactJson from "@microlink/react-json-view";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import Modal from "./modal";
import {
  jsonViewerSubject,
  closeJsonViewer,
  type JsonViewerConfig,
} from "../../services/json-viewer-service";
import "./json-viewer-modal.scss";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} role="tabpanel" className="json-tab-panel">
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

/**
 * Modal component for viewing JSON with tree view and formatted display.
 * Triggers from RxJS subject jsonViewerSubject.
 */
const DEFAULT_WIDTH = 820;
const DEFAULT_HEIGHT = 560;

export const JsonViewerModal: React.FC = () => {
  const [config, setConfig] = useState<JsonViewerConfig | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [modalPosition, setModalPosition] = useState({
    left: "50%",
    top: "50%",
  });
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const centerFor = (w: number, h: number) => ({
    left: `${Math.max(0, (window.innerWidth - w) / 2)}px`,
    top: `${Math.max(0, (window.innerHeight - h) / 2)}px`,
  });

  useEffect(() => {
    const subscription = jsonViewerSubject.subscribe((newConfig) => {
      setConfig(newConfig);
      setTabValue(0); // Reset to first tab when opening
      setIsMaximized(false); // always open at the default size
      setModalPosition(centerFor(DEFAULT_WIDTH, DEFAULT_HEIGHT));
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleMaximize = () => {
    setIsMaximized((max) => {
      const next = !max;
      if (next) {
        setModalPosition({ left: "12px", top: "12px" });
      } else {
        setModalPosition(centerFor(DEFAULT_WIDTH, DEFAULT_HEIGHT));
      }
      return next;
    });
  };

  const handleCopyJson = () => {
    if (!config) return;

    const jsonString = JSON.stringify(config.data, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopyFeedback(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopyFeedback(false);
      }, 2000);
    });
  };

  if (!config) return null;

  const formattedJson = JSON.stringify(config.data, null, 2);
  const keyCount = Object.keys(config.data || {}).length;

  const maxSize = {
    width: `${window.innerWidth - 24}px`,
    height: `${window.innerHeight - 24}px`,
  };
  const size = isMaximized
    ? maxSize
    : { width: `${DEFAULT_WIDTH}px`, height: `${DEFAULT_HEIGHT}px` };

  return (
    <>
      {/* Click-away backdrop: clicking outside the panel closes the viewer. */}
      <div className="json-viewer-backdrop" onMouseDown={closeJsonViewer} />
      <Modal
      // Remount when toggling maximize so the new initialSize is applied (the
      // Modal sets size from initialSize on mount and then mutates the DOM).
      key={isMaximized ? "max" : "normal"}
      id="json-viewer-modal"
      header={
        <div className="json-viewer-header">
          <span className="json-viewer-title">{config.title}</span>
          <span className="json-viewer-info">({keyCount} keys)</span>
          <div className="json-viewer-header-actions">
            {/* Close on the left, maximize/restore in the far-right corner. */}
            <button
              type="button"
              className="json-viewer-hdr-btn"
              onClick={closeJsonViewer}
              title="Close"
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="json-viewer-hdr-btn"
              onClick={toggleMaximize}
              title={isMaximized ? "Restore" : "Maximize"}
              aria-label={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </button>
          </div>
        </div>
      }
      hideClose
      onClose={closeJsonViewer}
      scrollContent={true}
      resizable={true}
      dragCallback={(pos) =>
        setModalPosition({
          left: `${pos.left}px`,
          top: `${pos.top}px`,
        })
      }
      initialSize={size}
      initialPosition={modalPosition}
      modalOnly={false}
    >
      <div className="json-viewer-container">
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          className="json-viewer-tabs"
        >
          <Tab label="Formatted JSON" />
          <Tab label="Tree View" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <div className="json-formatted-view">
            <div className="json-formatted-toolbar">
              <Tooltip title={copyFeedback ? "Copied!" : "Copy to clipboard"}>
                <IconButton
                  size="small"
                  onClick={handleCopyJson}
                  className={copyFeedback ? "copy-success" : ""}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            <SyntaxHighlighter
              language="json"
              style={vscDarkPlus}
              customStyle={{
                fontSize: "12px",
                padding: "12px",
                borderRadius: "4px",
                overflow: "auto",
                maxHeight: "440px",
              }}
              wrapLongLines={true}
            >
              {formattedJson}
            </SyntaxHighlighter>
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <div className="json-tree-view">
            <ReactJson
              src={config.data as object}
              theme="monokai"
              collapsed={false}
              name={false}
              indentWidth={2}
              displayDataTypes={false}
              displayObjectSize={true}
              quotesOnKeys={false}
              enableClipboard={true}
              // Render arrays as a normal vertical list instead of grouping them
              // into horizontal [0-100][100-200]… buckets (the default groups
              // arrays after 100 items, which looks awful in a narrow panel).
              groupArraysAfterLength={10000}
              style={{
                fontSize: "13px",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                padding: "12px",
                backgroundColor: "transparent",
                overflow: "auto",
                maxHeight: "440px",
              }}
            />
          </div>
        </TabPanel>
      </div>
    </Modal>
    </>
  );
};

export default JsonViewerModal;
